// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import SectionWrapper from "@adl/App/Components/Common/SectionWrapper";
import XngCarousel, { SwiperStyle } from "@adl/App/Components/XngCarousel/XngCarousel";
import { CustomAppState } from "@adl/App/Store/Reducers";
import RecommendedProductItem from "@adl/App/Widgets/RecomendedProducts/RecomendedProductItem";
import RecommendedProductsGridItem from "@adl/App/Widgets/RecomendedProducts/RecommendedProductsGridItem";
import { color, generateSlidesPerViewMap, stringToKeyGenerator, xScreen } from "@adl/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import loadProductInfoList from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/LoadProductInfoList";
import { getProductsForProductInfoList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tab, { TabPresentationProps } from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const enum tabFields {
    title = "title",
    products = "products",
}

const enum fields {
    tabs = "tabs",
    title = "title",
}

interface TabsProps {
    fields: {
        title: string;
        products: {
            fields: {
                productId: string;
            };
        }[];
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.tabs]: TabsProps[];
    };
}

const mapStateToProps = (state: CustomAppState) => {
    return {
        isTabletScreen: state.xCommon.isTabletScreen,
    };
};

const mapDispatchToProps = {
    loadProductInfoList,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RecommendedProductsStyles {
    container?: GridContainerProps;
    gridItem?: GridItemProps;
    title?: TypographyProps;
    centeringWrapper?: InjectableCss;
    innerWrapper?: InjectableCss;
    tabGroupStyle?: TabGroupPresentationProps;
    tabStyle?: TabPresentationProps;
    swiperStyles?: SwiperStyle;
    productCategoriesSelect?: SelectPresentationProps;
}

export const recommendedProductsStyles: RecommendedProductsStyles = {
    container: {
        gap: 24,
    },
    gridItem: {
        width: [12, 6, 6, 4, 4],
        css: css`
            ${xScreen("lte", "xs")} {
                justify-content: center;
            }
        `,
    },
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            flex-direction: column;
        `,
    },
    innerWrapper: {
        css: css`
            display: flex;
            flex-wrap: wrap;
            justify-content: flex-start;
        `,
    },
    tabGroupStyle: {
        mergeCss: true,
        cssOverrides: {
            tabContent: css`
                padding: 22px;
                ${xScreen("lte", "sm")} {
                    padding: 16px 0 25px;
                }
            `,
        },
    },
    tabStyle: {
        mergeCss: true,
        css: css`
            &:focus {
                border-color: ${color("common.ui10", 0.5)} ${color("common.ui5")} transparent;
            }
        `,
    },
    productCategoriesSelect: {
        cssOverrides: {
            formField: css`
                ${xScreen("lte", "md")} {
                    width: 260px;
                }
                ${xScreen("lte", "xs")} {
                    width: 100%;
                }
            `,
        },
    },
};

const styles = recommendedProductsStyles;
const slidesPerView = generateSlidesPerViewMap();
const RecommendedProducts: FC<Props> = ({ id, fields, loadProductInfoList, isTabletScreen }) => {
    const products = useAppSelector(state => getProductsForProductInfoList(state, id));
    const [tabProducts, setTabProducts] = useState<{ title: string; products: string[] }[]>([]);
    const [selectedTabOption, setSelectedTabOption] = useState(stringToKeyGenerator(fields.tabs?.[0]?.fields?.title));

    useEffect(() => {
        const productsIdsByTab: typeof tabProducts = [];
        const allProductIds: string[] = [];

        fields.tabs.forEach(tab => {
            const { fields } = tab;
            const tabProductsIds = fields.products?.map(product => {
                allProductIds.push(product.fields.productId);
                return product.fields.productId;
            });

            // Hide tab when it doesn't have products in CMS
            if (tabProductsIds.length > 0) {
                productsIdsByTab.push({ title: fields.title, products: tabProductsIds });
            }
        });

        setTabProducts(productsIdsByTab);

        if (allProductIds.length === 0) {
            return;
        }

        loadProductInfoList({
            id,
            getProductCollectionParameter: { productNumbers: allProductIds, pageSize: "9999" } as any,
        });
    }, [fields]);

    if (tabProducts.length === 0 || !products || products?.length === 0) {
        return null;
    }

    const handleSelectProductsTab: React.ChangeEventHandler<HTMLSelectElement> = event => {
        setSelectedTabOption(event.currentTarget.value);
    };

    if (isTabletScreen) {
        const tab = tabProducts.find(tab => stringToKeyGenerator(tab.title) === selectedTabOption);
        const productsByTab = products?.filter(product => tab?.products?.includes(product.productNumber));

        return (
            <SectionWrapper title={fields.title} data-test-selector="recommnededProducts">
                <StyledWrapper>
                    <Select
                        label={translate("Products")}
                        onChange={handleSelectProductsTab}
                        {...styles.productCategoriesSelect}
                    >
                        {tabProducts.map(tab => {
                            const value = stringToKeyGenerator(tab.title);
                            return (
                                <option key={value} value={value}>
                                    {tab.title}
                                </option>
                            );
                        })}
                    </Select>
                    <XngCarousel
                        navigation={true}
                        breakpoints={slidesPerView}
                        spaceBetween={22}
                        extendedStyles={styles.swiperStyles}
                    >
                        {productsByTab?.map(product => (
                            <RecommendedProductsGridItem key={product.id} product={product} widgetId={id} />
                        ))}
                    </XngCarousel>
                </StyledWrapper>
            </SectionWrapper>
        );
    }

    return (
        <SectionWrapper title={fields.title} data-test-selector="recommnededProducts">
            <TabGroup current={tabProducts[0]?.title} {...styles.tabGroupStyle}>
                {tabProducts.map(tab => {
                    const productsByTab = products?.filter(product => tab.products?.includes(product.productNumber));
                    return (
                        <Tab key={tab.title} headline={tab.title} tabKey={tab.title} {...styles.tabStyle}>
                            {isTabletScreen ? (
                                <XngCarousel
                                    navigation={true}
                                    breakpoints={slidesPerView}
                                    spaceBetween={22}
                                    extendedStyles={styles.swiperStyles}
                                >
                                    {productsByTab?.map(product => (
                                        <RecommendedProductItem key={product.id} product={product} widgetId={id} />
                                    ))}
                                </XngCarousel>
                            ) : (
                                <GridContainer {...styles.container}>
                                    {productsByTab?.map(product => (
                                        <GridItem {...styles.gridItem} key={product.id}>
                                            <RecommendedProductItem product={product} widgetId={id} />
                                        </GridItem>
                                    ))}
                                </GridContainer>
                            )}
                        </Tab>
                    );
                })}
            </TabGroup>
        </SectionWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(RecommendedProducts),
    definition: {
        displayName: "Recommended Products",
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.tabs,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.title;
                },
                fieldType: "General",
                isRequired: true,
                validate: value => (value.length > 5 ? translate("You Can Add 5 Tabs Only") : null),
                fieldDefinitions: [
                    {
                        name: tabFields.title,
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: tabFields.products,
                        editorTemplate: "ListField",
                        defaultValue: [],
                        getDisplay: (item: HasFields) => {
                            return item.fields.productId;
                        },
                        validate: value => (value.length > 6 ? translate("You Can Add 6 Products Only") : null),
                        fieldDefinitions: [
                            {
                                name: "productId",
                                editorTemplate: "TextField",
                                defaultValue: "",
                            },
                        ],
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
